import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, ShieldCheck, Clock, Lock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { deDate } from "@/lib/format";

const STATE_TEXT = {
  free: { title: "Dauerhaft kostenlos", text: "Ihr Konto ist dauerhaft freigeschaltet – kein Abo nötig.", icon: ShieldCheck, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  trial: { title: "Kostenlose Testphase", text: "Sie können FakturaEasy uneingeschränkt testen.", icon: Clock, cls: "text-blue-700 bg-blue-50 border-blue-200" },
  active: { title: "Abo aktiv", text: "Vielen Dank! Sie haben vollen Zugriff.", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  expired: { title: "Nur-Lesen-Modus", text: "Ihre Testphase ist abgelaufen. Ansehen und PDFs laden geht weiterhin – zum Anlegen und Bearbeiten benötigen Sie ein Abo.", icon: Lock, cls: "text-red-700 bg-red-50 border-red-200" },
};

function useConfirmReturn(checkAuth) {
  const [params, setParams] = useSearchParams();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const status = params.get("paypal");
    const subId = params.get("subscription_id");
    if (status === "cancel") toast.info("PayPal-Vorgang abgebrochen.");
    if (status === "success" && subId) {
      api.post("/billing/confirm", { subscription_id: subId })
        .then(() => { toast.success("Abo erfolgreich abgeschlossen!"); checkAuth(); })
        .catch((e) => toast.error(apiErr(e)));
    }
    if (status) setParams({}, { replace: true });
  }, [params, setParams, checkAuth]);
}

export default function Subscription() {
  const { user, billing, checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  useConfirmReturn(checkAuth);
  const s = STATE_TEXT[billing?.state] || STATE_TEXT.trial;
  const Icon = s.icon;
  const isActiveSub = billing?.subscription_status === "ACTIVE";

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/subscribe");
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Abo wirklich kündigen? Sie behalten vollen Zugriff bis zum Ende des bezahlten Zeitraums.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Abo gekündigt.");
      await checkAuth();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Abo</h1>
        <p className="text-sm text-slate-500 mt-1">Ihr FakturaEasy-Zugang für {user?.email}</p>
      </div>

      <div className={`rounded-2xl border p-5 flex gap-4 ${s.cls}`} data-testid="subscription-state">
        <Icon className="h-6 w-6 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <div className="font-semibold" data-testid="subscription-state-title">{s.title}</div>
          <p className="text-sm opacity-90">{s.text}</p>
          {billing?.state === "trial" && (
            <p className="text-sm font-medium" data-testid="trial-days-left">Noch {billing.trial_days_left} Tage (bis {deDate(billing.trial_end)})</p>
          )}
          {billing?.state === "active" && billing.paid_until && (
            <p className="text-sm font-medium" data-testid="paid-until">
              {isActiveSub ? "Nächste Abbuchung" : "Gekündigt – Zugriff bis"}: {deDate(billing.paid_until)}
            </p>
          )}
        </div>
      </div>

      {billing?.state !== "free" && !isActiveSub && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="subscription-offer">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-4xl font-extrabold tracking-tight">2,50 €</span>
            <span className="text-slate-500">/ Monat</span>
          </div>
          <ul className="text-sm text-slate-600 space-y-1.5">
            <li>Unbegrenzt Rechnungen, Kunden und Mahnungen</li>
            <li>E-Mail-Versand, Exporte und USt.-Quartalsübersicht</li>
            <li>Monatlich kündbar über PayPal</li>
          </ul>
          {billing?.state === "trial" && (
            <p className="text-xs text-slate-500">Die erste Abbuchung erfolgt erst nach Ende Ihrer Testphase.</p>
          )}
          <button
            onClick={subscribe}
            disabled={busy}
            data-testid="paypal-subscribe-button"
            className="w-full sm:w-auto bg-[#ffc439] hover:bg-[#f2b600] text-slate-900 font-semibold px-6 py-3 rounded-lg inline-flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-60"
          >
            <CreditCard className="h-4 w-4" /> {busy ? "Weiterleitung zu PayPal…" : "Mit PayPal abonnieren"}
          </button>
        </div>
      )}

      {isActiveSub && (
        <button onClick={cancel} disabled={busy} data-testid="cancel-subscription-button" className="text-sm text-slate-500 hover:text-red-600 underline underline-offset-4 disabled:opacity-60">
          Abo kündigen
        </button>
      )}
    </div>
  );
}
